<script lang="ts">
    import { KEY_CODE_ARROW_LEFT, KEY_CODE_ARROW_RIGHT } from "@/constants";
    import ArrowIcon from "@apps/instagram/widgets/instagram_gallery_widget/components/icons/ArrowIcon.svelte";
    import InstagramBranding from "@apps/instagram/widgets/instagram_gallery_widget/components/InstagramBranding.svelte";
    import TikTokPostEmbed from "@apps/tiktok/widgets/tiktok_gallery_widget/components/TikTokPostEmbed.svelte";
    import { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import { createEventDispatcher, tick } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import Scrollable from "@components/Scrollable.svelte";
    import T from "@components/T.svelte";

    import TikTokIcon from "../icons/TikTokIcon.svelte";
    import TikTokPostModalManager from "./TikTokPostModalManager";
    import TikTokPostModalTagsList from "./TikTokPostModalTagsList.svelte";

    export let tikTokPostModalManager: TikTokPostModalManager;
    export let urlsBuilder: URLsBuilder;
    export let productQuickViewEnabled: boolean;
    export let hideBranding: boolean;

    const activePost$ = tikTokPostModalManager.activePost$;
    const selectedTagId$ = tikTokPostModalManager.selectedTagId$;
    let hasNext = true;
    let hasPrev = true;
    const dispatch = createEventDispatcher();
    let modalElement: null | HTMLDivElement = null;
    const handleNext = () => {
        tikTokPostModalManager.next();
    };
    const handlePrev = () => {
        tikTokPostModalManager.prev();
    };
    const handleKeydown = (e: KeyboardEvent) => {
        const key = e.which || e.keyCode;
        if (key === KEY_CODE_ARROW_LEFT) {
            handlePrev();
        } else if (key === KEY_CODE_ARROW_RIGHT) {
            handleNext();
        }
    };
    const handleTagSelect = (e: CustomEvent) => {
        tikTokPostModalManager.selectTag(e.detail);
    };
    const handleTagUnSelect = () => {
        tikTokPostModalManager.unSelectTag();
    };
    const handleProductTagOpen = (e: CustomEvent<number>) => {
        tikTokPostModalManager.openProductTag(e.detail);
    };
    const handleClose = () => {
        dispatch("close");
    };
    const setFocus = async () => {
        await tick();
        modalElement?.focus();
    };
    $: {
        $activePost$;
        if ($activePost$) {
            setFocus();
        }
    }
</script>

<div
    tabindex="-1"
    on:keydown={handleKeydown}
    bind:this={modalElement}
    class="gw-tt-post-modal-post-view"
>
    {#if $activePost$}
        <div class="gw-tt-post-modal-post-view__left">
            {#key $activePost$}
                <TikTokPostEmbed post={$activePost$} />
            {/key}
        </div>
        <div class="gw-tt-post-modal-post-view__right">
            <div class="gw-tt-post-modal-post-view__header">
                <a
                    href={$activePost$.postUrl}
                    class="gw-tt-post-modal-post-view__tt-link gw-link-reset-default-styles"
                    rel="noopener noreferrer"
                    target="_blank"
                    aria-label="Opens post on tiktok in a new tab"
                >
                    <div class="gw-tt-post-modal-post-view__tt-link-icon-wrap">
                        <Icon
                            class="gw-tt-post-modal-post-view__tt-link-icon"
                            width={13}
                            height={13}
                        >
                            <TikTokIcon />
                        </Icon>
                    </div>
                    <T
                        namespace="tiktok.critical.TikTokGalleryWidget"
                        translationKey="view_on_tiktok"
                    />
                </a>
                <BaseButton
                    class="gw-tt-post-modal-post-view__close"
                    ariaLabel="Close post modal"
                    on:click={handleClose}
                >
                    <Icon width={12} height={12}>
                        <CloseIcon />
                    </Icon>
                </BaseButton>
            </div>
            <Scrollable class="gw-tt-post-modal-post-view__right-scrolle">
                <div class="gw-tt-post-modal-post-view__right-content">
                    <TikTokPostModalTagsList
                        on:tag_select={handleTagSelect}
                        on:tag_unselect={handleTagUnSelect}
                        on:tag_open={handleProductTagOpen}
                        selectedTagId={$selectedTagId$}
                        activeMediaTags={$activePost$.tags}
                        {productQuickViewEnabled}
                        {urlsBuilder}
                    />
                    {#if !hideBranding}
                        <InstagramBranding position="center" />
                    {/if}
                </div>
            </Scrollable>
        </div>

        {#if hasPrev}
            <BaseButton
                ariaLabel="prev tiktok slide"
                class="gw-ig-post-modal-viewer__arrow gw-ig-post-modal-viewer__arrow--prev"
                on:click={handlePrev}
            >
                <Icon width={20} height={20}>
                    <ArrowIcon />
                </Icon>
            </BaseButton>
        {/if}
        {#if hasNext}
            <BaseButton
                ariaLabel="next tiktok slide"
                class="gw-ig-post-modal-viewer__arrow gw-ig-post-modal-viewer__arrow--next"
                on:click={handleNext}
            >
                <Icon width={20} height={20}>
                    <ArrowIcon />
                </Icon>
            </BaseButton>
        {/if}
    {/if}
</div>

<style>
    .gw-tt-post-modal-post-view :global(.gw-ig-post-modal-viewer__arrow) {
        display: flex;
        position: absolute;
        z-index: 3;
        top: 280px;
        padding: 6px;
        transform: translateY(-50%);
        border-radius: 50%;
        opacity: 0.6;
        background: var(--gw-lightgrey-bg);
        color: var(--gw-grey-text-color);
    }

    :global(.gw-ig-post-modal-viewer__arrow--prev) {
        left: -42px;
        transform: translateY(-50%) rotateZ(180deg);
    }

    :global(.gw-ig-post-modal-viewer__arrow--next) {
        right: -42px;
    }

    .gw-tt-post-modal-post-view {
        display: grid;
        width: 100%;
        grid-template-columns: minmax(0, 1.5fr) 2fr;
        outline: none;
        outline-offset: none;
    }
    .gw-tt-post-modal-post-view__left {
        display: flex;
        position: relative;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }
    .gw-tt-post-modal-post-view__right {
        display: flex;
        position: relative;
        flex-direction: column;
        min-height: 500px;
    }

    .gw-tt-post-modal-post-view__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.8em;
    }

    .gw-tt-post-modal-post-view__tt-link {
        display: flex;
        align-items: center;
        padding: 8px;
        column-gap: 10px;
        border-radius: var(--gw-border-radius-base);
        background: var(--gw-dark-bg);
        color: var(--gw-white-text-color);
        font-size: 0.8em;
        line-height: 0;
    }

    .gw-tt-post-modal-post-view__tt-link-icon-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: var(--gw-white-bg);
        color: var(--gw-dark-text-color);
    }

    .gw-tt-post-modal-post-view__header
        :global(.gw-tt-post-modal-post-view__close) {
        color: var(--gw-modal-close-icon-color);
    }

    .gw-tt-post-modal-post-view__right
        :global(.gw-tt-post-modal-post-view__right-scrolle) {
        position: relative;
        height: 100%;
    }
    .gw-tt-post-modal-post-view__right-content {
        position: absolute;
        width: 100%;
    }
    @media all and (max-width: 970px) {
        .gw-tt-post-modal-post-view {
            font-size: 0.9em;
        }
    }

    @media all and (max-width: 768px) {
        .gw-tt-post-modal-post-view {
            grid-template-columns: 1fr;
        }

        .gw-tt-post-modal-post-view__right {
            min-height: unset;
        }

        .gw-tt-post-modal-post-view__right-content {
            position: relative;
        }
    }
</style>
